if (!customElements.get('og-custom-widget')) {
  class OgCustomWidget extends HTMLElement {
    constructor () {
      super()
      this.selectedVariant = null
      this.selectedVariantChewCount = 0
      this.selectedChewsPerDay = 0
      this.recommendedSellingPlan = null
      this.handleVariantChange = this.handleVariantChange.bind(this)
      this.handleFrequencyChange = this.handleFrequencyChange.bind(this)
      this.handleSubscribeChange = this.handleSubscribeChange.bind(this)
      this.handleCustomButtonClick = this.handleCustomButtonClick.bind(this)
    }

    connectedCallback () {
      this._disconnected = false

      this.parentWrapper = this.closest('og-offer')

      /* --- Scoped Container ---
       * og-custom-widget lives in og-offer's shadow DOM. Walk up via the shadow
       * root host to find the light-DOM block container, so queries for
       * #og-custom-buttons and [data-variant-metafields] are scoped to the
       * correct product instead of grabbing the first match on the page.
       */
      const ogOfferEl = this.getRootNode()?.host
      this.blockContainer = ogOfferEl?.closest('[data-block-type="custom-ordergroove-widget"]') || document

      /* --- Elements & Data --- */

      // Custom Buttons
      const documentCustomButtonsEl = this.blockContainer.querySelector('#og-custom-buttons')
      const customButtons = documentCustomButtonsEl ? documentCustomButtonsEl.content.cloneNode(true) : null
      this.customButtonsContainer = this.querySelector('og-custom-buttons')
      if (this.customButtonsContainer && customButtons) {
        this.customButtonsContainer.appendChild(customButtons)
      }

      // Variant Metafields that Map Variant IDs to Chew Counts
      const variantMetaFieldMapEl = this.blockContainer.querySelector('[data-variant-metafields]')
      this.variantMetaFieldMap = variantMetaFieldMapEl ? JSON.parse(variantMetaFieldMapEl.textContent) : {}

      // Subscribe Checkbox & Opt-in/out Buttons
      this.subscribeCheckbox = this.querySelector('#og-subscribe-checkbox')
      this.optOutButton = this.querySelector('og-optout-button')
      this.optInButton = this.querySelector('og-optin-button')

      // Pricing Elements
      this.productInfoEl = document.querySelector('.product__info')
      this.regularPriceEl = this.querySelector('og-price[regular]')
      this.subscriptionPriceEl = this.querySelector('og-price[subscription]')

      /* --- Event Listeners --- */

      // Listen for user interaction with our custom checkbox
      if (this.subscribeCheckbox) {
        this.subscribeCheckbox.addEventListener('change', this.handleSubscribeChange)
      }

      // Listen for clicks on our custom buttons
      if (this.customButtonsContainer) {
        this.customButtonsContainer.addEventListener('click', this.handleCustomButtonClick)
      }

      // Observe the opt-in button for changes to the 'subscribed' attribute so we can keep our checkbox in sync
      this.observeWidgetUpdates()

      // On load, check the URL for a variant ID and set the chew count accordingly
      this.setVariantIDFromURL()

      // The frequency select lives inside nested shadow DOMs (og-select-frequency > og-select > select)
      // that OG populates asynchronously after connectedCallback fires. Poll until ready.
      this.initFrequencySelect()
    }

    disconnectedCallback () {
      this._disconnected = true
      this.subscribeCheckbox?.removeEventListener('change', this.handleSubscribeChange)
      this.customButtonsContainer?.removeEventListener('click', this.handleCustomButtonClick)
      this.frequencySelect?.removeEventListener('change', this.handleFrequencyChange)
      this.widgetObserver?.disconnect()
      this.priceObserver?.disconnect()
      this.widgetObserver = null
      this.priceObserver = null
    }

    initFrequencySelect () {
      let attempts = 0
      const maxAttempts = 40 // ~2s at rAF rate (~50ms each)

      const tryInit = () => {
        if (this._disconnected) return

        const frequencySelectorEl = this.querySelector('og-select-frequency')
        const frequencySelectEl = frequencySelectorEl?.shadowRoot?.querySelector('og-select')
        const select = frequencySelectEl?.shadowRoot?.querySelector('select')

        if (select) {
          this.frequencySelect = select
          frequencySelectEl.style.width = '100%' // Fill entire width inside shadow root
          this.frequencySelect.addEventListener('change', this.handleFrequencyChange)
          return
        }

        if (++attempts < maxAttempts) {
          requestAnimationFrame(tryInit)
        }
      }

      requestAnimationFrame(tryInit)
    }

    setVariantIDFromURL () {
      const urlParams = new URLSearchParams(window.location.search)
      const variantID = urlParams.get('variant')
      if (variantID) {
        this.selectedVariant = variantID
        this.setChewCountFromVariantID()
      }
    }

    setChewCountFromVariantID () {
      const chewCountKey = Object.keys(this.variantMetaFieldMap).find(key => key.includes(this.selectedVariant))
      const fallbackChewCount = Object.values(this.variantMetaFieldMap)[0]?.['chews_per_package'] ?? 0
      this.selectedVariantChewCount = chewCountKey ? this.variantMetaFieldMap[chewCountKey]['chews_per_package'] : fallbackChewCount
    }

    handleSubscribeChange (event) {
      const isChecked = event.target.checked
      if(isChecked) {
        this.optInButton?.click()
        this.reHydrateButtonStates()
      } else {
        this.optOutButton?.click()
        this.updatePricing()
      }
    }

    handleVariantChange (variantID) {
      if (variantID) {
        this.selectedVariant = variantID
        this.setChewCountFromVariantID()
        this.reHydrateButtonStates()
      }
    }

    handleFrequencyChange (event) {
      // Our custom buttons include the sellingPlanID when updating frequency, if it's missing, reset the button states.
      if (!event.detail?.sellingPlanID) this.resetButtonStates()
    }

    handleCustomButtonClick (event) {
      if (!this.customButtonsContainer) return
      const button = event.target.closest('button[data-chews-per-day]')
      if (button) {
        this.selectedChewsPerDay = parseInt(button.getAttribute('data-chews-per-day'), 10)
        if (!Number.isFinite(this.selectedChewsPerDay) || this.selectedChewsPerDay <= 0) return
        this.customButtonsContainer.querySelectorAll('button[data-chews-per-day]').forEach(btn => {
          btn.setAttribute('aria-pressed', 'false')
        })
        button.setAttribute('aria-pressed', 'true')

        // Loop through the frequency options and find the best one based on how long the chews will last
        const daysChewsLast = Math.ceil(this.selectedVariantChewCount / this.selectedChewsPerDay)
        if (this.frequencySelect) {
          const options = this.frequencySelect.querySelectorAll('option')
          const optionsSortedByDays = Array.from(options).sort((a, b) => {
            const aDays = parseInt(a?.querySelector('og-text')?.getAttribute('pluralize') || '0')
            const bDays = parseInt(b?.querySelector('og-text')?.getAttribute('pluralize') || '0')
            return aDays - bDays
          })
          this.recommendedSellingPlan = optionsSortedByDays[0]?.value
          optionsSortedByDays.forEach(option => {
            const sellingPlanID = option.value
            const daysBetweenDeliveries = option?.querySelector('og-text')?.getAttribute('pluralize')

            if (sellingPlanID && daysBetweenDeliveries) {
              const daysBetween = parseInt(daysBetweenDeliveries)
              if (daysChewsLast >= daysBetween) {
                this.recommendedSellingPlan = sellingPlanID
              }
            }
          })
          this.frequencySelect.value = this.recommendedSellingPlan
          this.frequencySelect.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            detail: { sellingPlanID: this.frequencySelect.value }
          }))
        }
      }
    }

    updatePricing () {
      if (!this.productInfoEl) return

      const priceListEl = this.productInfoEl.querySelector('product-meta .price-list')
      if (!priceListEl) return

      const regularPriceValue = this.parsePriceValue(this.regularPriceEl)
      const subscriptionPriceValue = this.parsePriceValue(this.subscriptionPriceEl)

      if (this.subscribeCheckbox.checked && Number.isFinite(subscriptionPriceValue) && Number.isFinite(regularPriceValue)) {
        priceListEl.innerHTML = `
          <span class="price price--highlight">
            <span class="visually-hidden">Sale price</span>
            $${subscriptionPriceValue.toFixed(2)}
          </span>
          <span class="price price--compare">
            <span class="visually-hidden">Regular price</span>
            $${regularPriceValue.toFixed(2)}
          </span>
        `
      } else if (!this.subscribeCheckbox.checked && Number.isFinite(regularPriceValue)) {
        priceListEl.innerHTML = `
          <span class="price">
            <span class="visually-hidden">Regular price</span>
            $${regularPriceValue.toFixed(2)}
          </span>
        `
      }

      this.updatePricePerUnit()
    }

    updatePricePerUnit () {
      const pricePerUnitEls = document.querySelectorAll('.price-per-package')
      if (!pricePerUnitEls.length) return

      const regularPriceValue = this.parsePriceValue(this.regularPriceEl)
      const subscriptionPriceValue = this.parsePriceValue(this.subscriptionPriceEl)
      const isSubscribed = this.subscribeCheckbox?.checked && Number.isFinite(subscriptionPriceValue) && Number.isFinite(regularPriceValue) && regularPriceValue > 0
      const discountRatio = isSubscribed ? subscriptionPriceValue / regularPriceValue : 1

      pricePerUnitEls.forEach(el => {
        if (el.hasAttribute('data-no-subscription')) return
        const regularPrice = parseFloat(el.getAttribute('data-regular-price'))
        const unitsPerPackage = parseInt(el.getAttribute('data-units-per-package'), 10)
        if (!Number.isFinite(regularPrice) || !Number.isFinite(unitsPerPackage) || unitsPerPackage <= 0) return
        el.textContent = `$${(regularPrice * discountRatio / unitsPerPackage).toFixed(2)}/ea`
      })
    }

    parsePriceValue (priceEl) {
      const rawText = priceEl?.shadowRoot?.textContent?.trim()
      if (!rawText) return NaN
      return parseFloat(rawText.replace(/[^0-9.]/g, ''))
    }

    reHydrateButtonStates () {
      if (!this.customButtonsContainer) return
      const button = this.customButtonsContainer.querySelector('button[data-chews-per-day][aria-pressed="true"]')
      setTimeout(() => {button?.click()}, 0)
    }

    resetButtonStates () {
      if (!this.customButtonsContainer) return
      const buttons = this.customButtonsContainer.querySelectorAll('button[data-chews-per-day]')
      buttons.forEach(btn => btn.setAttribute('aria-pressed', 'false'))
    }

    syncCheckboxToSubscribed () {
      if (!this.subscribeCheckbox) return
      this.subscribeCheckbox.checked = this.parentWrapper.hasAttribute('subscribed')
      console.log('Syncing checkbox to subscribed state:', this.subscribeCheckbox.checked)
      this.updatePricing()
    }

    observeWidgetUpdates () {
      if (!this.parentWrapper) return

      this.widgetObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'subscribed') {
            this.syncCheckboxToSubscribed()
          }
          if (m.type === 'attributes' && m.attributeName === 'product') {
            const newVariantID = m.target.getAttribute('product')
            if (this.selectedVariant !== newVariantID) {
              this.handleVariantChange(newVariantID)
            }
          }
        }
      })

      this.widgetObserver.observe(this.parentWrapper, {
        attributes: true,
        attributeFilter: ['subscribed', 'product'],
      })

      // Observe the subscription price element for changes so we can update the displayed price accordingly
      this.priceObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'childList' || m.type === 'characterData') {
            this.updatePricing()
          }
        }
      })

      if (this.subscriptionPriceEl?.shadowRoot) {
        this.priceObserver.observe(this.subscriptionPriceEl.shadowRoot, {
          childList: true,
          characterData: true,
          subtree: true,
        })
      }

      this.syncCheckboxToSubscribed()
    }
  }

  customElements.define('og-custom-widget', OgCustomWidget)
}
